import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import {
  TCrop,
  TDestinationPartner,
  TTradeDetailsById,
  TTradeDocument,
} from "../types/trades/trades";
import moment from "moment";

export type TAddDispatchDetails = {
  dispatchDate: string;
  expectedDelivaryDate: string;
};

export type TCropApiData = {
  baseName: string;
  cropId: number;
  cropName: string;
  imageUrl: string;
  type: string;
  units: string;
  varietyName: string;
  qtyUnit: string;
  rateType: string | number | "";
  qty: number | "";
  weight: number | "";
  freightAmt: number | "";
  status: number;
  tradeId: number;
  weightUnit: string;
  id: number;
  destWeight: number | "";
  addWastage: number | "";
  destRate: number | "";
  rate: number | "";
  unitType?: string;
  destBayerWeight?: number | "";
  bayerWeight?: number | "";
  landingRate: number | "";
  packaging: string;
  qltyGrade: string;
  variety: string;
  size: string;
  rateCategory: string | number | "";
};
export type TGaadiProviderData = {
  tid: number;
  name: string;
  firmName: string;
  mobile: string;
  profilePic: string;
};
export type TAddDispatchDocuments = {
  tapal: TTradeDocument[];
  receipt: TTradeDocument[];
  other: TTradeDocument[];
};

export type TAllDispatchInputs = {
  freightAmt: number;
  freightAdv: number;
  invoiceAmt: number;
  tradeComments: string;
  srcVehNum: string;
  commodityCost: number;
  driverName: string;
  driverMobile: number;
  miscCost: number;
  containerId: string | number;
  tradeMode: string;
  totalWeight: number;
  rate: number;
  helperName: string;
  helperNumber: string;
  dispatchTime: string;
  dispatchDate: string;
  deliveryDate: string;
  rateUnit: string;
  totalQty: number;
  pickDate: string;
  pickTime: string;
  gaadiProviderObj: TGaadiProviderData;
};

type TinitialState = {
  partyData: TDestinationPartner[];
  selectedParty: TDestinationPartner | null;
  selectedSrcParty: TDestinationPartner | null;
  selectedDate: string;
  selectedCrop: TCrop | null;
  deletedCrop: TCrop | null;
  tradeEditStatus: boolean;
  dispatchDocs: TAddDispatchDocuments;
  dispatchInputs: TAllDispatchInputs;
  cropApiData: TCropApiData;
  cropLineItems: TCrop[];
  gaadiProviderData: TGaadiProviderData[];
  freightCostUpdated: boolean;
};

const initialState: TinitialState = {
  partyData: [],
  selectedParty: null,
  selectedSrcParty: null,
  selectedDate: moment().format("YYYY-MM-DD"),
  selectedCrop: null,
  deletedCrop: null,
  tradeEditStatus: false,

  dispatchDocs: {
    tapal: [],
    receipt: [],
    other: [],
  },
  dispatchInputs: {
    freightAmt: 0,
    invoiceAmt: 0,
    tradeComments: "",
    srcVehNum: "",
    commodityCost: 0,
    driverName: "",
    driverMobile: 0,
    miscCost: 0,
    containerId: 0,
    freightAdv: 0,
    tradeMode: "",
    totalWeight: 0,
    rate: 0,
    helperName: "",
    helperNumber: "",
    dispatchTime: "",
    dispatchDate: "",
    deliveryDate: "",
    rateUnit: "",
    totalQty: 0,
    pickDate: "",
    pickTime: "",
    gaadiProviderObj: {
      tid: 0,
      name: "",
      firmName: "",
      mobile: "",
      profilePic: "",
    },
  },
  cropApiData: {
    baseName: "",
    cropId: 0,
    cropName: "",
    imageUrl: "",
    type: "",
    units: "",
    varietyName: "",
    qtyUnit: "",
    rateType: "",
    qty: 0,
    weight: 0,
    freightAmt: 0,
    status: 0,
    tradeId: 0,
    weightUnit: "",
    id: 0,
    destWeight: 0,
    addWastage: 0,
    destRate: 0,
    rate: 0,
    unitType: undefined,
    destBayerWeight: undefined,
    bayerWeight: undefined,
    landingRate: 0,
    packaging: "",
    qltyGrade: "",
    variety: "",
    size: "",
    rateCategory: "",
  },
  cropLineItems: [],
  gaadiProviderData: [],
  freightCostUpdated: false,
};

export const dispatchDetails = createSlice({
  name: "dispatchDetails",
  initialState: initialState,
  reducers: {
    restDispatchDetails: (state) => {
      return {
        ...initialState,
      };
    },
    updatePartyData: (
      state,
      action: PayloadAction<{
        partyData: TDestinationPartner[];
      }>
    ) => {
      state.partyData = action.payload.partyData;
    },
    updateCropData: (
      state,
      action: PayloadAction<{
        selectedCrop: TCrop | null;
        selectedParty?: TDestinationPartner | null;
        deletedCrop?: TCrop | null;
        selectedSrcParty?: TDestinationPartner | null;
      }>
    ) => {
      if (action.payload.selectedParty != null) {
        state.selectedParty = action.payload.selectedParty;
      }
      if (action.payload.selectedSrcParty != null) {
        state.selectedSrcParty = action.payload.selectedSrcParty;
      }
      state.selectedCrop = action.payload.selectedCrop;
      if (action.payload.deletedCrop) {
        state.deletedCrop = action.payload.deletedCrop;
      }
    },

    editTrade: (
      state,
      action: PayloadAction<{
        tradeEditStatus: boolean;
      }>
    ) => {
      state.tradeEditStatus = action.payload.tradeEditStatus;
    },
    selectedDispatchDate: (state, action) => {
      state.selectedDate = action.payload;
    },

    setAddSrcDispatchDate: (
      state,
      action: PayloadAction<{
        tradeDetail: TTradeDetailsById;
      }>
    ) => {
      let tradeDetail = action.payload.tradeDetail;
      state.dispatchInputs.dispatchDate = tradeDetail?.date;
      state.dispatchDocs = {
        ...state.dispatchDocs,
        tapal: [],
        receipt: [],
        other: [],
      };
    },

    setCropApiData: (
      state,
      action: PayloadAction<{
        tradeDetail: TTradeDetailsById;
      }>
    ) => {
      let tradeDetail = action.payload.tradeDetail.lineItems[0];
      state.cropApiData.imageUrl = tradeDetail?.imageUrl;
      state.cropApiData.landingRate = tradeDetail?.landingRate;
      state.cropApiData.rate = tradeDetail?.rate;
      state.cropApiData.qty = tradeDetail?.qty;
      state.cropApiData.weight = tradeDetail?.weight;
    },

    setUpdatedCropData: (
      state,
      action: PayloadAction<{
        cropApiData: TCrop | null;
      }>
    ) => {
      state.cropApiData = action.payload.cropApiData!;
    },

    setAddDispatchInput: (
      state,
      action: PayloadAction<{
        tradeDetail: TTradeDetailsById;
      }>
    ) => {
      let tradeDetail = action.payload.tradeDetail;
      state.dispatchInputs.freightAmt = Number(tradeDetail?.freightAmt);
      state.dispatchInputs.invoiceAmt = Number(tradeDetail?.invAmt);
      state.dispatchInputs.tradeComments =
        tradeDetail?.progress?.find((p) => p.progState === "PENDING")
          ?.comments || "";
      state.dispatchInputs.srcVehNum = tradeDetail?.freightDtls?.srcVehNum;
      state.dispatchInputs.commodityCost = Number(tradeDetail?.commodityCost);
      state.dispatchInputs.driverName = tradeDetail?.freightDtls?.srcDriverName;
      state.dispatchInputs.driverMobile = Number(
        tradeDetail?.freightDtls?.srcDriverMobile
      );
      state.dispatchInputs.miscCost = Number(tradeDetail?.misc);
      state.dispatchInputs.containerId = tradeDetail?.freightDtls.containerId;
      state.dispatchInputs.freightAdv = Number(tradeDetail?.freightAdv);
      state.dispatchInputs.tradeMode = tradeDetail?.tradeMode;
      state.dispatchInputs.rate = Number(tradeDetail?.freightDtls?.rate);
      state.dispatchInputs.rateUnit = tradeDetail?.freightDtls?.rateUnit;
      state.dispatchInputs.pickDate = tradeDetail?.pickDate;
      state.dispatchInputs.pickTime = tradeDetail?.pickTime;
      state.dispatchInputs.gaadiProviderObj!.name =
        tradeDetail?.freightDtls.transName;
      state.dispatchInputs.gaadiProviderObj!.tid = tradeDetail?.freightDtls.tid;
      state.dispatchInputs.gaadiProviderObj!.mobile =
        tradeDetail?.freightDtls.transMobile;
      state.dispatchInputs.helperName = tradeDetail?.freightDtls?.helperName;
      state.dispatchInputs.helperNumber =
        tradeDetail?.freightDtls?.helperMobile;
      state.dispatchInputs.deliveryDate = tradeDetail?.deliveryDate;
      state.dispatchInputs.dispatchTime = tradeDetail?.dispatchTime;
    },

    setTapalDocuments: (state, action: PayloadAction<TTradeDocument[]>) => {
      state.dispatchDocs.tapal = action.payload;
    },

    setReceiptDocuments: (state, action: PayloadAction<TTradeDocument[]>) => {
      state.dispatchDocs.receipt = action.payload;
    },

    setOtherDocuments: (state, action: PayloadAction<TTradeDocument[]>) => {
      state.dispatchDocs.other = action.payload;
    },

    udpateAddDispatchDetails: (
      state,
      action: PayloadAction<{
        dispatch: TAllDispatchInputs;
      }>
    ) => {
      state.dispatchInputs = action.payload.dispatch;
    },
    // new
    setCropLineItems: (state, action: PayloadAction<TCrop[]>) => {
      state.cropLineItems = action.payload;
    },
    setGaadiProviderData: (
      state,
      action: PayloadAction<TGaadiProviderData[]>
    ) => {
      state.gaadiProviderData = action.payload;
    },
    setFreightCostInputChange: (state, action: PayloadAction<boolean>) => {
      state.freightCostUpdated = action.payload;
    },
  },
});

export const {
  updatePartyData,
  updateCropData,
  selectedDispatchDate,
  setAddSrcDispatchDate,
  setTapalDocuments,
  setOtherDocuments,
  setReceiptDocuments,
  setAddDispatchInput,
  setCropApiData,
  setUpdatedCropData,
  udpateAddDispatchDetails,
  setCropLineItems,
  setGaadiProviderData,
  setFreightCostInputChange,
} = dispatchDetails.actions;
export default dispatchDetails.reducer;
